import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRightIcon,
  FileTextIcon,
  GlobeIcon,
  LifeBuoyIcon,
  LogOutIcon,
  MapPinIcon,
  NewspaperIcon,
  PaperPlaneIcon,
  PlusSquareIcon,
  ShoppingBasketIcon,
  UserIcon,
  WarehouseIcon,
} from '@/shared/ui/icons';
import { PageHeader } from '@/shared/ui/layout/PageHeader';
import {
  FeaturesSettingsLanguageDialog,
  FeaturesSettingsSettingsRow,
  useCurrentLanguage,
} from '@/features/settings';
import { useLogout } from '@/features/auth';
import { FeaturesCargoIdProfileCargoIdBanner } from '@/features/cargo-id';
import { useCurrentUser } from '@/entities/auth';

export const WidgetsSettingsMain = () => {
  const { t } = useTranslation('settings');
  const navigate = useNavigate();
  const logout = useLogout();
  const currentLanguage = useCurrentLanguage();
  const [languageOpen, setLanguageOpen] = useState(false);
  const profile = useCurrentUser();
  const avatarUrl = profile.data?.avatar_url ?? null;

  return (
    <>
      <PageHeader title={t('title')} />
      <div className="flex flex-col gap-4 p-4 md:mx-auto md:w-full md:max-w-2xl lg:p-6">
        <button
          type="button"
          onClick={() => navigate('/settings/profile')}
          className="flex w-full cursor-pointer items-center gap-3 overflow-hidden rounded-2xl border border-slate-200 bg-white p-4 text-left shadow-card transition-colors hover:bg-slate-50"
        >
          <span className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-slate-100 text-ink-faint">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
            ) : (
              <UserIcon className="h-7 w-7" aria-hidden />
            )}
          </span>
          <span className="min-w-0 flex-1">
            {profile.isLoading ? (
              <span className="block h-4 w-40 max-w-full animate-pulse rounded bg-slate-200" />
            ) : profile.data?.full_name ? (
              <>
                <span className="block truncate text-base font-semibold text-ink">
                  {profile.data.full_name}
                </span>
                <span className="block truncate text-xs text-ink-muted">
                  {profile.data.email}
                </span>
              </>
            ) : (
              <span className="block truncate text-sm font-medium text-ink">
                {profile.data?.email ?? ''}
              </span>
            )}
          </span>
          <ChevronRightIcon className="h-4 w-4 shrink-0 text-ink-faint" aria-hidden />
        </button>

        <FeaturesCargoIdProfileCargoIdBanner />

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card">
          <FeaturesSettingsSettingsRow
            icon={<MapPinIcon className="h-5 w-5" />}
            label={t('rows.addresses')}
            onClick={() => navigate('/settings/addresses')}
          />
          <div className="border-t border-slate-100" />
          <FeaturesSettingsSettingsRow
            icon={<FileTextIcon className="h-5 w-5" />}
            label={t('payments:title')}
            onClick={() => navigate('/settings/payments')}
          />
          <div className="border-t border-slate-100" />
          <FeaturesSettingsSettingsRow
            icon={<WarehouseIcon className="h-5 w-5" />}
            label={t('rows.onlineAddress')}
            onClick={() => navigate('/settings/online-address')}
          />
          <div className="border-t border-slate-100" />
          <FeaturesSettingsSettingsRow
            icon={<PlusSquareIcon className="h-5 w-5" />}
            label={t('services:additionalServices')}
            onClick={() => navigate('/settings/services')}
          />
          <div className="border-t border-slate-100" />
          <FeaturesSettingsSettingsRow
            icon={<LifeBuoyIcon className="h-5 w-5" />}
            label={t('services:techHelper')}
            onClick={() => navigate('/support-staff')}
          />
          <div className="border-t border-slate-100" />
          <FeaturesSettingsSettingsRow
            icon={<ShoppingBasketIcon className="h-5 w-5" />}
            label={t('rows.wikishop')}
            disabled
            showChevron={false}
            trailing={
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-muted">
                {t('rows.comingSoon')}
              </span>
            }
          />
          <div className="border-t border-slate-100" />
          <FeaturesSettingsSettingsRow
            icon={<PaperPlaneIcon className="h-5 w-5" />}
            label={t('rows.telegramBot')}
            disabled
            showChevron={false}
            trailing={
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-muted">
                {t('rows.comingSoon')}
              </span>
            }
          />
          <div className="border-t border-slate-100" />
          <FeaturesSettingsSettingsRow
            icon={<NewspaperIcon className="h-5 w-5" />}
            label={t('rows.telegramChannel')}
            disabled
            showChevron={false}
            trailing={
              <span className="shrink-0 rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-ink-muted">
                {t('rows.comingSoon')}
              </span>
            }
          />
          <div className="border-t border-slate-100" />
          <FeaturesSettingsSettingsRow
            icon={<GlobeIcon className="h-5 w-5" />}
            label={t('rows.language')}
            trailing={
              <span className="flex shrink-0 items-center gap-2 text-sm text-ink-muted">
                <currentLanguage.Flag className="h-5 w-7 shrink-0 rounded-sm" aria-hidden />
                {currentLanguage.native}
              </span>
            }
            onClick={() => setLanguageOpen(true)}
          />
        </div>

        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-card">
          <FeaturesSettingsSettingsRow
            icon={<LogOutIcon className="h-5 w-5" />}
            label={t('common:logout')}
            danger
            showChevron={false}
            onClick={() => void logout.logout()}
          />
        </div>
      </div>

      <FeaturesSettingsLanguageDialog open={languageOpen} onClose={() => setLanguageOpen(false)} />
    </>
  );
};
